import { apiFetch, apiJson } from "../api/client";

export interface AdminUser {
  user_id: number;
  google_id: string;
  name: string;
  email: string;
  picture?: string;
  roles?: string[];
  bought_pixels?: number;
  first_purchase?: string | null;
  last_login?: string | null;
  created_at?: string | null;
  pending_orders?: number;
}

export interface AdminConnection {
  websocket_id: number;
  username: string;
  id: number;
  roles?: string[];
  session_id?: string;
  ip_address?: string;
  seen?: number;
  geodata?: {
    city?: string;
    country?: string;
  };
}

export interface IpBan {
  ip_address: string;
  banned_at?: string | null;
  banned_by_user_id?: number | null;
  banned_by_name?: string | null;
  banned_by_email?: string | null;
  comment?: string | null;
}

export interface AdminRegion {
  id: string;
  name: string;
  priority: number;
  is_global?: boolean;
  min_lat?: number;
  max_lat?: number;
  min_lng?: number;
  max_lng?: number;
  properties?: {
    price?: number | Record<string, number>;
    [k: string]: unknown;
  };
  tags?: Record<string, unknown>;
  /** Vertex count in list view; full ring in detail view */
  original_geojson?: number | Array<[number, number]>;
}

export type AdminRegionDetail = AdminRegion & {
  original_geojson: Array<[number, number]>;
};

export const DEFAULT_PIXEL_CAPACITY = 1_000_000;

export interface AdminRegionStats {
  id: string;
  name: string;
  is_global?: boolean;
  total_pixels: number | null;
  sold_pixels: number;
  sold_pct: number | null;
}

export interface AdminStats {
  pixels_by_status: Record<string, number>;
  sold_total: number;
  reserved_total: number;
  claimed_total: number;
  available_total: number;
  sold_by_day: Array<{ date: string; count: number }>;
  sold_by_month: Array<{ month: string; count: number }>;
  revenue: Array<{ currency: string; orders: number; amount: number }>;
  users_total: number;
  buyers_total: number;
  orders_paid: number;
  active_connections: number;
  active_users: number;
}

export interface ReservedPixel {
  pixel: number;
  user_id?: number;
  status?: 0 | 1 | 2;
  user_name?: string;
  user_email?: string;
  updated?: string;
}

export interface SoldPixel {
  pixel: number;
  user_id?: number;
  user_name?: string;
  user_email?: string;
  bought_at?: string;
  image?: string;
  color?: string;
  latitude?: number;
  longitude?: number;
  location?: string;
  country?: string;
  region_name?: string;
  region?: {
    name?: string;
    global?: boolean;
    properties?: Record<string, unknown>;
  };
  frameName?: string;
  artistName?: string;
  frameColor?: string;
  info?: { lat: number; lng: number };
  ownership_status?: "owned" | "available_again";
  admin_action_id?: number | null;
  deleted_at?: string | null;
  deleted_by_name?: string | null;
  delete_comment?: string | null;
  can_undo?: boolean;
}

export interface AdminPixelAction {
  action_id: number;
  action_type: string;
  created_at?: string | null;
  comment?: string | null;
  pixel_id?: number | null;
  previous_owner_user_id?: number | null;
  owner_name?: string | null;
  owner_email?: string | null;
  admin_user_id?: number | null;
  admin_name?: string | null;
  admin_email?: string | null;
  status: "active" | "undone" | string;
  undone_at?: string | null;
  undone_by_name?: string | null;
  undone_by_email?: string | null;
  can_undo: boolean;
  current_pixel_status?: number | null;
  location?: string;
  country?: string;
  region_name?: string;
  bought_at?: string | null;
  image?: string | null;
}

export type ContactStatus = "new" | "open" | "resolved";

export interface AdminContactSummary {
  contact_id: number;
  user_id: number;
  status: ContactStatus;
  created_at?: string | null;
  updated_at?: string | null;
  name: string;
  email: string;
  subject: string;
  username?: string | null;
  ip_address?: string | null;
}

export interface AdminContactDetail extends AdminContactSummary {
  message: string;
  x_forwarded_for?: string | null;
  user_agent?: string | null;
  resolved_at?: string | null;
  resolved_by_user_id?: number | null;
  resolved_by_name?: string | null;
}

async function adminJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(`/admin/${path}`, init);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  const body = (await res.json()) as { status?: string; data?: T };
  return (body.data ?? body) as T;
}

export async function fetchAdminStats(): Promise<AdminStats> {
  return adminJson<AdminStats>("stats");
}

export async function fetchAdminRegionStats(): Promise<AdminRegionStats[]> {
  return adminJson<AdminRegionStats[]>("regions/stats");
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  return adminJson<AdminUser[]>("users");
}

export async function updateAdminUserRoles(
  userId: number,
  roles: string[],
): Promise<void> {
  const res = await apiFetch(`/admin/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify({ roles }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
}

export async function resetAdminUserCheckout(
  userId: number,
): Promise<{ orders_aborted: number; pixels_released: number }> {
  return adminJson(`users/${userId}/checkout/reset`, { method: "POST" });
}

export async function fetchAdminConnections(): Promise<AdminConnection[]> {
  return adminJson<AdminConnection[]>("connections");
}

export async function fetchAdminRegions(): Promise<AdminRegion[]> {
  return adminJson<AdminRegion[]>("regions");
}

export async function fetchAdminRegionDetail(
  id: string,
): Promise<AdminRegionDetail> {
  return adminJson<AdminRegionDetail>(`regions/${id}`);
}

export async function upsertGlobalRegion(payload: {
  name?: string;
  priority?: number;
  properties: AdminRegion["properties"];
}): Promise<string> {
  const res = await apiFetch("/admin/regions/global", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { id?: string };
  return body.id ?? "";
}

export async function updateAdminRegion(
  id: string,
  payload: Partial<Pick<AdminRegion, "name" | "priority" | "properties" | "tags">>,
): Promise<void> {
  const res = await apiFetch(`/admin/regions/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function fetchReservedPixels(): Promise<ReservedPixel[]> {
  return adminJson<ReservedPixel[]>("pixels/reserved");
}

export async function fetchAvailablePixels(): Promise<ReservedPixel[]> {
  return adminJson<ReservedPixel[]>("pixels/available");
}

export async function fetchSoldPixels(limit = 100): Promise<SoldPixel[]> {
  return adminJson<SoldPixel[]>(`pixels/sold?limit=${limit}`);
}

export async function deleteSoldPixel(
  pixelId: number,
  comment: string,
): Promise<AdminPixelAction> {
  const res = await apiFetch(`/admin/pixels/sold/${pixelId}/delete`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) {
    throw new Error(await readAdminError(res));
  }
  const body = (await res.json()) as { data?: AdminPixelAction };
  if (!body.data) throw new Error("Missing response data");
  return body.data;
}

export async function fetchAdminPixelActions(
  limit = 200,
): Promise<AdminPixelAction[]> {
  return adminJson<AdminPixelAction[]>(`pixels/actions?limit=${limit}`);
}

export async function undoAdminPixelAction(
  actionId: number,
): Promise<AdminPixelAction> {
  const res = await apiFetch(`/admin/pixels/actions/${actionId}/undo`, {
    method: "POST",
  });
  if (!res.ok) {
    throw new Error(await readAdminError(res));
  }
  const body = (await res.json()) as { data?: AdminPixelAction };
  if (!body.data) throw new Error("Missing response data");
  return body.data;
}

async function readAdminError(res: Response): Promise<string> {
  const text = await res.text().catch(() => "");
  if (!text) return `HTTP ${res.status}`;
  try {
    const body = JSON.parse(text) as { message?: string; reason?: string };
    return body.message || body.reason || text;
  } catch {
    return text;
  }
}

export async function releaseReservedPixel(pixelId: number): Promise<void> {
  const res = await apiFetch(`/admin/pixels/reserved/${pixelId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function releaseAllReservedPixels(): Promise<number> {
  const res = await apiFetch("/admin/pixels/reserved", { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { released?: number };
  return body.released ?? 0;
}

export async function terminateConnection(connectionId: string | number): Promise<void> {
  const res = await apiFetch(`/admin/connections/${connectionId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function fetchIpBans(): Promise<IpBan[]> {
  return adminJson<IpBan[]>("ip-bans");
}

export async function banIp(ipAddress: string, comment: string): Promise<void> {
  const res = await apiFetch("/admin/ip-bans", {
    method: "POST",
    body: JSON.stringify({ ip_address: ipAddress, comment }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
}

export async function unbanIp(ipAddress: string): Promise<void> {
  const res = await apiFetch(`/admin/ip-bans/${encodeURIComponent(ipAddress)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function verifyAdminAccess(): Promise<boolean> {
  try {
    await apiJson("/auth/me?role=admin");
    return true;
  } catch {
    return false;
  }
}

export async function fetchAdminContacts(): Promise<AdminContactSummary[]> {
  return adminJson<AdminContactSummary[]>("contacts");
}

export async function fetchAdminContactDetail(
  contactId: number,
): Promise<AdminContactDetail> {
  return adminJson<AdminContactDetail>(`contacts/${contactId}`);
}

export async function updateAdminContactStatus(
  contactId: number,
  status: ContactStatus,
): Promise<AdminContactDetail> {
  const res = await apiFetch(`/admin/contacts/${contactId}`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  const body = (await res.json()) as { data?: AdminContactDetail };
  if (!body.data) throw new Error("Missing response data");
  return body.data;
}

export interface BlackFramePreview {
  eligible_count: number;
  palette: string[];
}
export const fetchBlackFramePreview = () => adminJson<BlackFramePreview>("pixels/black-frames");
export async function randomizeBlackFrames(expectedCount: number): Promise<{ updated: number; action_id: number | null }> {
  const res = await apiFetch("/admin/pixels/black-frames/randomize", {
    method: "POST", body: JSON.stringify({ expected_count: expectedCount }),
  });
  if (!res.ok) throw new Error(await readAdminError(res));
  return (await res.json()).data;
}
